import {gsap} from 'gsap'
import {useMemo, useRef, type RefCallback} from 'react'

import type {Metric} from '../Index.types'

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const getMetricId = ({label, value}: Metric) => JSON.stringify([label, value])

const parseMetricValue = (value: string, locale: string) => {
  // Five digits ensure the sample has a grouping separator, even in locales such as Spanish.
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6)
  const groupSeparator = parts.find(part => part.type === 'group')?.value
  const decimalSeparator = parts.find(part => part.type === 'decimal')?.value

  if (!groupSeparator || !decimalSeparator) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console -- Authoring feedback only; do not report cosmetic failures in production.
      console.warn(`Missing number separators for locale "${locale}"; skipping metric animation.`)
    }
    return
  }

  // Accept ordinary, non-breaking, and narrow non-breaking spaces where the locale groups with spaces.
  const groupPattern = /\s/.test(groupSeparator) ? '[ \\u00a0\\u202f]' : escapeRegExp(groupSeparator)
  const numberPattern = new RegExp(`\\d+(?:${groupPattern}\\d{3})*(?:${escapeRegExp(decimalSeparator)}\\d+)?`, 'g')
  const groupRegExp = new RegExp(groupPattern, 'g')
  const formatParts: Array<(progress: number) => string> = []
  let endIndex = 0

  for (const match of value.matchAll(numberPattern)) {
    const matchedNumber = match[0]
    const inputGroupSeparator = matchedNumber.match(groupRegExp)?.[0]
    const normalizedNumber = matchedNumber.replace(groupRegExp, '').replace(decimalSeparator, '.')
    const targetValue = Number(normalizedNumber)

    if (!Number.isFinite(targetValue)) return

    // Older Intl implementations allow at most 20 fractional digits.
    const fractionDigits = Math.min(matchedNumber.split(decimalSeparator)[1]?.length ?? 0, 20)
    const formatter = new Intl.NumberFormat(locale, {
      useGrouping: inputGroupSeparator !== undefined,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
    const prefix = value.slice(endIndex, match.index)
    endIndex = match.index + matchedNumber.length

    formatParts.push(progress => {
      const formattedNumber = formatter
        .format(progress * targetValue)
        .replaceAll(groupSeparator, inputGroupSeparator ?? groupSeparator)

      return prefix + formattedNumber
    })
  }

  if (formatParts.length === 0) return

  const suffix = value.slice(endIndex)
  const formatValue = (progress: number) => formatParts.map(formatPart => formatPart(progress)).join('') + suffix

  return {formatValue}
}

const animateMetric = (
  element: HTMLSpanElement,
  id: string,
  value: string,
  locale: string,
  prefersReducedMotion: boolean,
  threshold: number,
  startedMetrics: Set<string>,
) => {
  if (prefersReducedMotion) {
    startedMetrics.add(id)
  }

  if (startedMetrics.has(id)) {
    element.textContent = value
    return
  }

  const parsedValue = parseMetricValue(value, locale)
  if (!parsedValue) {
    element.textContent = value
    return
  }

  const counter = {progress: 0}
  let tween: gsap.core.Tween | undefined

  const updateText = () => {
    element.textContent = parsedValue.formatValue(counter.progress)
  }

  const observer = new IntersectionObserver(
    entries => {
      // Another copy of this metric may have started while this element was waiting off screen.
      if (startedMetrics.has(id)) {
        observer.disconnect()
        element.textContent = value
        return
      }

      const isVisible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= threshold)
      if (!isVisible) return

      observer.disconnect()
      startedMetrics.add(id)

      tween = gsap.to(counter, {
        progress: 1,
        duration: 2,
        ease: 'expo.out',
        onUpdate: updateText,
        onComplete: () => {
          // Restore the original text, including its exact formatting.
          element.textContent = value
        },
      })
    },
    {threshold: [0, threshold]},
  )

  updateText()
  observer.observe(element)

  return () => {
    observer.disconnect()
    tween?.kill()
    element.textContent = value
  }
}

export const useAnimatedMetrics = (
  metrics: readonly Metric[],
  locale: string,
  prefersReducedMotion: boolean,
  threshold = 0.5,
) => {
  // Keep this above the individual elements so recycled marquee cards do not restart their counters.
  const startedMetricsRef = useRef(new Set<string>())

  return useMemo(() => {
    const refEntries = metrics.map(metric => {
      const {value} = metric
      const id = getMetricId(metric)

      const ref: RefCallback<HTMLSpanElement> = element => {
        if (!element) return

        return animateMetric(element, id, value, locale, prefersReducedMotion, threshold, startedMetricsRef.current)
      }

      return [id, ref] as const
    })

    const metricRefs = new Map(refEntries)
    return (metric: Metric) => metricRefs.get(getMetricId(metric))
  }, [metrics, locale, prefersReducedMotion, threshold, startedMetricsRef])
}
